import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  ColumnFiltersState,
  OnChangeFn,
  PaginationState,
} from '@tanstack/react-table';
import './DataTable.css';

/**
 * DataTable — shared data-table component for the crypto-trading UI.
 *
 * Canonical pattern (CSS lifted from the original `OrdersTable.css`):
 * - Filter chrome at the top (.table-filters with .filter-group children)
 * - Sticky-header table with sortable columns (.data-table)
 * - Optional global search input
 * - Optional server pagination footer
 * - Loading / error / empty states render in place of the table body
 *
 * Modes:
 * - Client-side filtering: pass `columnFilters` + `onColumnFiltersChange` (controlled)
 *   AND keep `data` as the full set. TanStack handles filtering.
 * - Server-side filtering: omit `columnFilters`. Parent is responsible for fetching
 *   already-filtered `data` and reflecting filter selections via `filters[].onChange`.
 *
 * Pagination:
 * - Omit `serverPagination` → no pagination, all rows render.
 * - Provide `serverPagination` → controlled by parent. The component renders Prev/Next +
 *   page-size selector and binds them to the parent's onChange callbacks.
 */

export interface DataTableFilter {
  /** unique id within the filter row */
  id: string;
  /** visible label rendered before the select */
  label: string;
  /** current filter value (controlled). Use '' or 'ALL' for the "no filter" state. */
  value: string;
  /** dropdown options. The component prepends an "All" entry automatically. */
  options: { value: string; label: string }[];
  /** receives '' when the operator picks "All", otherwise the chosen option's value */
  onChange: (value: string) => void;
}

export interface DataTableSearch {
  /** input placeholder text */
  placeholder?: string;
  /** controlled value */
  value: string;
  /** controlled onChange */
  onChange: (value: string) => void;
}

export interface DataTableServerSorting {
  /** controlled SortingState. Single-column for now. */
  state: SortingState;
  /** receives the new state. */
  onChange: (s: SortingState) => void;
}

export interface DataTableServerPagination {
  /** 0-based page index */
  pageIndex: number;
  /** rows per page */
  pageSize: number;
  /** total page count, or -1 if unknown / loading */
  pageCount: number;
  /** total rows (for the "Showing X–Y of N" footer) */
  totalRows?: number;
  /** receives the new 0-based page index */
  onPageIndexChange: (index: number) => void;
  /** optional page-size change handler */
  onPageSizeChange?: (size: number) => void;
  /** options for the page-size select; defaults to [25, 50, 100, 200] */
  pageSizeOptions?: number[];
}

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  /** initial sort. Updates internally via onSortingChange. */
  defaultSorting?: SortingState;
  /** filter dropdowns rendered in the .table-filters row. */
  filters?: DataTableFilter[];
  /** controlled TanStack columnFilters for client-side filtering. Omit for server-filtered data. */
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  /** controlled global search. Omit to hide the search input. */
  search?: DataTableSearch;
  /** Provide for server-paginated tables. */
  serverPagination?: DataTableServerPagination;
  /**
   * Provide to make sorting controlled by the parent (and expected to be applied
   * server-side). When omitted, sorting is internal and applies only to the
   * current data slice — fine for client-paginated tables but misleading for
   * server-paginated ones.
   */
  serverSorting?: DataTableServerSorting;
  isLoading?: boolean;
  /** message shown when isLoading. Defaults to "Loading…". */
  loadingMessage?: string;
  isError?: boolean;
  /** message shown when isError. Auto-prefixed with "Error: " unless `errorMessagePrefix` is set to ''. */
  errorMessage?: string;
  /** prefix for the error row. Defaults to "Error: ". Pass '' to disable. */
  errorMessagePrefix?: string;
  /** rendered when data is empty (after filtering) and not loading/erroring */
  emptyMessage?: string;
  ariaLabel?: string;
  /** optional callback that returns extra className(s) for the row's <tr> (e.g. buy/sell side) */
  rowClassName?: (row: T) => string | undefined;
  /** rendered on the right of the .table-filters row (e.g. "Showing N of M") */
  filterInfo?: React.ReactNode;
  /** rendered far-right of the .table-filters row — typically a primary action button
   *  (e.g. "New X"). Use `modal-pill-button modal-pill-primary` for constructive actions. */
  headerActions?: React.ReactNode;
  /** when provided, rows become clickable (Enter/Space + click invoke this handler) */
  onRowClick?: (row: T) => void;
  /** when this returns non-null for a row, an expanded row is rendered immediately after */
  expandedRowContent?: (row: T) => React.ReactNode | null;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

export function DataTable<T>({
  data,
  columns,
  defaultSorting,
  filters,
  columnFilters,
  onColumnFiltersChange,
  search,
  serverPagination,
  serverSorting,
  isLoading = false,
  loadingMessage = 'Loading…',
  isError = false,
  errorMessage,
  errorMessagePrefix = 'Error: ',
  emptyMessage = 'No rows match the selected filters.',
  ariaLabel,
  rowClassName,
  filterInfo,
  headerActions,
  onRowClick,
  expandedRowContent,
}: DataTableProps<T>) {
  const [internalSorting, setInternalSorting] = useState<SortingState>(defaultSorting ?? []);
  const [internalGlobalFilter, setInternalGlobalFilter] = useState('');

  // serverSorting takes precedence: state is controlled by the parent, sort is
  // expected to be applied in the SQL query (manualSorting: true).
  const sorting = serverSorting ? serverSorting.state : internalSorting;
  const setSorting = serverSorting ? serverSorting.onChange : setInternalSorting;
  const isServerSorted = serverSorting !== undefined;
  const isServerPaginated = serverPagination !== undefined;
  const pagination: PaginationState | undefined = isServerPaginated
    ? { pageIndex: serverPagination.pageIndex, pageSize: serverPagination.pageSize }
    : undefined;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      ...(columnFilters !== undefined ? { columnFilters } : {}),
      ...(search !== undefined ? { globalFilter: search.value } : { globalFilter: internalGlobalFilter }),
      ...(pagination !== undefined ? { pagination } : {}),
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(next);
    },
    onColumnFiltersChange,
    onGlobalFilterChange: search ? undefined : setInternalGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    // For server-sorted tables, the data array is already in server-sort order;
    // skip TanStack's client-side getSortedRowModel so it doesn't reshuffle.
    getSortedRowModel: isServerSorted ? undefined : getSortedRowModel(),
    getFilteredRowModel: columnFilters !== undefined || search ? getFilteredRowModel() : undefined,
    manualSorting: isServerSorted,
    manualPagination: isServerPaginated,
    pageCount: isServerPaginated ? serverPagination.pageCount : undefined,
  });

  const showFilterRow = (filters && filters.length > 0) || search !== undefined || filterInfo !== undefined || headerActions !== undefined;
  const rows = table.getRowModel().rows;

  const pageSizeOptions = useMemo(
    () => serverPagination?.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS,
    [serverPagination?.pageSizeOptions]
  );

  return (
    <div className="data-table-container" aria-label={ariaLabel}>
      {showFilterRow && (
        <div className="table-filters">
          {filters?.map((f) => (
            <div className="filter-group" key={f.id}>
              <label htmlFor={`filter-${f.id}`}>{f.label}:</label>
              <select
                id={`filter-${f.id}`}
                value={f.value || 'ALL'}
                onChange={(e) => {
                  const v = e.target.value;
                  f.onChange(v === 'ALL' ? '' : v);
                }}
              >
                <option value="ALL">All</option>
                {f.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
          {search && (
            <div className="filter-group">
              <label htmlFor="data-table-search">Search:</label>
              <input
                id="data-table-search"
                type="text"
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                placeholder={search.placeholder ?? 'Search…'}
              />
            </div>
          )}
          {filterInfo && <div className="filter-info">{filterInfo}</div>}
          {headerActions && <div className="table-filters__actions">{headerActions}</div>}
        </div>
      )}

      <div className="table-wrapper">
        <table
          className="data-table"
          // aria-busy lets assistive tech announce that table contents are
          // changing while a refetch is in flight, instead of reading stale
          // rows. Pairs with the existing loadingMessage status row.
          aria-busy={isLoading || undefined}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  // Map TanStack's sort state to the WAI-ARIA sort attribute so
                  // screen readers announce which column is active and in which
                  // direction. 'none' is the default for sortable-but-unsorted
                  // columns; non-sortable columns omit the attribute entirely.
                  const ariaSort: 'ascending' | 'descending' | 'none' | undefined = canSort
                    ? sorted === 'asc'
                      ? 'ascending'
                      : sorted === 'desc'
                        ? 'descending'
                        : 'none'
                    : undefined;
                  return (
                    <th
                      key={header.id}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      className={canSort ? 'sortable' : ''}
                      title={canSort ? 'Click to sort' : undefined}
                      aria-sort={ariaSort}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {sorted && <span>{sorted === 'asc' ? ' ↑' : ' ↓'}</span>}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="data-table__status" colSpan={columns.length}>
                  {loadingMessage}
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td className="data-table__status data-table__status--error" colSpan={columns.length}>
                  {errorMessagePrefix}
                  {errorMessage ?? 'Failed to load data.'}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="data-table__status" colSpan={columns.length}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const extra = rowClassName?.(row.original);
                const isClickable = onRowClick !== undefined;
                const className = [extra, isClickable ? 'clickable-row' : undefined]
                  .filter(Boolean)
                  .join(' ') || undefined;
                const expanded = expandedRowContent?.(row.original);
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={className}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      onClick={isClickable ? () => onRowClick!(row.original) : undefined}
                      onKeyDown={
                        isClickable
                          ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onRowClick!(row.original);
                              }
                            }
                          : undefined
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                    </tr>
                    {expanded != null && (
                      <tr className="data-table__expanded-row">
                        <td colSpan={table.getVisibleLeafColumns().length}>{expanded}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {isServerPaginated && (
        <div className="data-table-pagination">
          <div className="pagination-info">
            {(() => {
              const total = serverPagination.totalRows ?? 0;
              if (total === 0) return 'No results';
              const start = serverPagination.pageIndex * serverPagination.pageSize + 1;
              const end = Math.min(start + rows.length - 1, total);
              return `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}`;
            })()}
          </div>
          <div className="pagination-controls">
            <button
              type="button"
              onClick={() => serverPagination.onPageIndexChange(0)}
              disabled={serverPagination.pageIndex === 0 || isLoading}
              aria-label="First page"
            >
              «
            </button>
            <button
              type="button"
              onClick={() => serverPagination.onPageIndexChange(serverPagination.pageIndex - 1)}
              disabled={serverPagination.pageIndex === 0 || isLoading}
              aria-label="Previous page"
            >
              ‹ Prev
            </button>
            <span className="pagination-page">
              {`Page ${serverPagination.pageIndex + 1}${serverPagination.pageCount > 0 ? ` of ${serverPagination.pageCount}` : ''}`}
            </span>
            <button
              type="button"
              onClick={() => serverPagination.onPageIndexChange(serverPagination.pageIndex + 1)}
              disabled={
                isLoading ||
                (serverPagination.pageCount > 0 &&
                  serverPagination.pageIndex >= serverPagination.pageCount - 1)
              }
              aria-label="Next page"
            >
              Next ›
            </button>
            <button
              type="button"
              onClick={() => serverPagination.onPageIndexChange(Math.max(0, serverPagination.pageCount - 1))}
              disabled={
                isLoading ||
                serverPagination.pageCount <= 0 ||
                serverPagination.pageIndex >= serverPagination.pageCount - 1
              }
              aria-label="Last page"
            >
              »
            </button>
            {serverPagination.onPageSizeChange && (
              <div className="filter-group pagination-page-size">
                <label htmlFor="data-table-page-size">Per page:</label>
                <select
                  id="data-table-page-size"
                  value={serverPagination.pageSize}
                  onChange={(e) => serverPagination.onPageSizeChange?.(Number(e.target.value))}
                >
                  {pageSizeOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
